import { Component, computed, inject, Type, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AppLayoutService } from '../../app-layout.service';
import { BarChartSquare02 } from '../../../../../shared/icons/bar-chart-square-02/bar-chart-square-02';
import { Cube01 } from '../../../../../shared/icons/cube-01/cube-01';
import { Clock } from '../../../../../shared/icons/clock/clock';
import { Calendar } from '../../../../../shared/icons/calendar/calendar';
import { Bell02 } from '../../../../../shared/icons/bell-02/bell-02';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../../../../shared/ui/button/button';
import { UserService } from '../../../../../shared/services/user.service';
import { Building05 } from '../../../../../shared/icons/building-05/building-05';
import { ChevronRight } from '../../../../../shared/icons/chevron-right/chevron-right';
import { UserCircle } from '../../../../../shared/icons/user-circle/user-circle';
import { Rectangle5 } from '../../../../../shared/icons/rectangle-5/rectangle-5';
import { DynamicIconComponent } from '../../../../../shared/ui/dynamic-icon/dynamic-icon';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../../../shared/services/auth.service';
import { LocationCompanyService } from '../../../../../shared/services/location-company.service';
import { LocationIndividualService } from '../../../../../shared/services/location-individual.service';
import { ClientPackageStateService } from '../../../../../shared/services/client-package-state.service';
import { NotificationService } from '../../../../../shared/services/notification.service';

interface NavItem {
  label: string;
  route: string;
  iconComponent: Type<any>;
}

const COMPANY_NAV: NavItem[] = [
  { label: 'Kontrolna tabla', route: '/company/dashboard', iconComponent: BarChartSquare02 },
  { label: 'Paketi', route: '/company/packages', iconComponent: Cube01 },
  { label: 'Moji termini', route: '/company/appointments', iconComponent: Clock },
  { label: 'Kalendar', route: '/company/calendar', iconComponent: Calendar },
];

const INDIVIDUAL_NAV: NavItem[] = [
  { label: 'Moji termini', route: '/individual/appointments', iconComponent: Clock },
  { label: 'Kupovina', route: '/individual/packages/offer', iconComponent: Cube01 },
  { label: 'Kalendar', route: '/individual/calendar', iconComponent: Calendar },
];

@Component({
  standalone: true,
  selector: 'app-client-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    Bell02,
    ButtonComponent,
    Building05,
    UserCircle,
    Rectangle5,
    DynamicIconComponent,
    Menu,
    ChevronRight,
  ],
  templateUrl: './client-header.html',
  styleUrl: './client-header.scss',
})
export class ClientHeader {
  @ViewChild('userMenu') userMenu!: Menu;

  userService = inject(UserService);
  sidebarService = inject(AppLayoutService);
  authService = inject(AuthService);
  private _companyLocation = inject(LocationCompanyService);
  private _individualLocation = inject(LocationIndividualService);
  private _packageState = inject(ClientPackageStateService);
  private _router = inject(Router);
  protected notificationService = inject(NotificationService);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  hoveredRoute: string | null = null;

  navItems = computed(() =>
    this.userService.isCompany() ? COMPANY_NAV : INDIVIDUAL_NAV
  );

  buttonLabel = computed(() =>
    this._packageState.canBook() ? 'Zakažite termin' : 'Kupite paket'
  );

  buttonIcon = computed(() =>
    this._packageState.canBook() ? Clock : Cube01
  );

  constructor() {
    this._packageState.load();
    this.notificationService.loadUnreadCount();
  }

  userMenuItems: MenuItem[] = [
    {
      label: 'Podešavanja',
      icon: 'pi pi-cog',
      command: () => {
        if (this.userService.isCompany()) {
          this._companyLocation.routeToSettings();
        } else {
          this._individualLocation.routeToSettings();
        }
      },
    },
    {
      separator: true,
    },
    {
      label: 'Odjavi se',
      className: 'hover:text-red-600',
      icon: 'pi pi-sign-out',
      command: () => this.authService.logout(),
    },
  ];

  toggleUserMenu(event: MouseEvent) {
    this.userMenu.toggle(event);
  }

  goToNotifications() {
    if (this.userService.isCompany()) {
      this._companyLocation.routeToNotifications();
    } else {
      this._individualLocation.routeToNotifications();
    }
  }

  onPrimaryAction() {
    const canBook = this._packageState.canBook();
    if (this.userService.isCompany()) {
      if (canBook) {
        this._companyLocation.routeToCreateAppointment();
      } else {
        this._companyLocation.routeToPackagesOffer();
      }
      return;
    }
    if (canBook) {
      this._individualLocation.routeToCreateAppointment();
    } else {
      this._router.navigate(['/individual/packages/offer']);
    }
  }
}
